import { forwardRef } from 'react';
import type { ButtonHTMLAttributes } from 'react';
import { cn } from '../lib/cn';

export type ButtonVariant = 'primary' | 'accent' | 'outline' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Render a full-width block button (common on mobile forms). */
  block?: boolean;
}

/**
 * shadcn/ui-style button. All colors come from token-backed Tailwind classes
 * (defined in the app's tailwind.config), so the brand palette can change
 * without editing this component. Generous tap targets for mobile field use.
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'bg-primary text-on-primary shadow-sm hover:bg-primary-hover hover:shadow-md focus-visible:ring-ring',
  accent: 'bg-accent text-on-accent shadow-sm hover:bg-accent-hover hover:shadow-md focus-visible:ring-ring',
  outline:
    'border border-border-strong bg-surface text-text-primary hover:bg-surface-muted hover:border-border-strong focus-visible:ring-ring',
  ghost: 'bg-transparent text-text-primary hover:bg-surface-muted focus-visible:ring-ring',
  danger: 'bg-danger text-on-danger shadow-sm hover:bg-danger-hover hover:shadow-md focus-visible:ring-danger',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-3 text-sm',
  md: 'h-11 px-4 text-sm',
  lg: 'h-12 px-6 text-base',
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', block = false, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-md font-medium',
        // Press feedback (scale on :active) makes the button feel like it heard the
        // tap; transform/box-shadow are GPU-cheap. Disabled under reduced-motion.
        'transition-[background-color,border-color,box-shadow,transform] duration-150 ease-out',
        'active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        'disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none',
        VARIANTS[variant],
        SIZES[size],
        block && 'w-full',
        className,
      )}
      {...rest}
    />
  );
});
